import React, { useState, useMemo } from "react";
import { Card, Col } from "react-bootstrap";
import { Building, CheckCircleFill, XCircleFill } from "react-bootstrap-icons";
import { useDispatch, useSelector } from "react-redux";
import { handleClaimDashboardFilter } from "../config/claimDashboardSlice";

const DEFAULT_PAYER_PERFORMANCE_DATA = [
  {
    payer: "Bupa Arabia",
    code: "BUPA",
    claimsCount: 4850,
    grossAmount: 1350000,
    netSubmitted: 1200000,
    approvedAmount: 1050000,
    rejectedAmount: 150000,
    pendingAmount: 65000,
    approvalRate: 87.5,
    rejectionRate: 12.5,
    avgClaimValue: 278.3,
  },
  {
    payer: "Tawuniya",
    code: "TAW",
    claimsCount: 3920,
    grossAmount: 1080000,
    netSubmitted: 980000,
    approvedAmount: 890000,
    rejectedAmount: 90000,
    pendingAmount: 50000,
    approvalRate: 90.8,
    rejectionRate: 9.2,
    avgClaimValue: 275.5,
  },
  {
    payer: "MedGulf",
    code: "MEDG",
    claimsCount: 2450,
    grossAmount: 650000,
    netSubmitted: 580000,
    approvedAmount: 510000,
    rejectedAmount: 70000,
    pendingAmount: 35000,
    approvalRate: 87.9,
    rejectionRate: 12.1,
    avgClaimValue: 265.3,
  },
  {
    payer: "GIG Saudi",
    code: "GIG",
    claimsCount: 1840,
    grossAmount: 490000,
    netSubmitted: 430000,
    approvedAmount: 385000,
    rejectedAmount: 45000,
    pendingAmount: 28000,
    approvalRate: 89.5,
    rejectionRate: 10.5,
    avgClaimValue: 266.3,
  },
  {
    payer: "Malath Insurance",
    code: "MAL",
    claimsCount: 1280,
    grossAmount: 340000,
    netSubmitted: 300000,
    approvedAmount: 268000,
    rejectedAmount: 32000,
    pendingAmount: 22000,
    approvalRate: 89.3,
    rejectionRate: 10.7,
    avgClaimValue: 265.6,
  },
  {
    payer: "Al Rajhi Takaful",
    code: "RAJ",
    claimsCount: 1150,
    grossAmount: 310000,
    netSubmitted: 275000,
    approvedAmount: 248000,
    rejectedAmount: 27000,
    pendingAmount: 18000,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 269.5,
  },
  {
    payer: "Allianz SF",
    code: "ALZ",
    claimsCount: 950,
    grossAmount: 245000,
    netSubmitted: 215000,
    approvedAmount: 194000,
    rejectedAmount: 21000,
    pendingAmount: 14000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 257.8,
  },
  {
    payer: "AXA Cooperative",
    code: "AXA",
    claimsCount: 820,
    grossAmount: 210000,
    netSubmitted: 185000,
    approvedAmount: 167000,
    rejectedAmount: 18000,
    pendingAmount: 12000,
    approvalRate: 90.2,
    rejectionRate: 9.8,
    avgClaimValue: 256.0,
  },
  {
    payer: "Walaa Insurance",
    code: "WLA",
    claimsCount: 710,
    grossAmount: 185200,
    netSubmitted: 162100,
    approvedAmount: 146000,
    rejectedAmount: 16100,
    pendingAmount: 10500,
    approvalRate: 90.1,
    rejectionRate: 9.9,
    avgClaimValue: 260.8,
  },
  {
    payer: "Arabian Shield",
    code: "ARS",
    claimsCount: 572,
    grossAmount: 140000,
    netSubmitted: 123000,
    approvedAmount: 107200,
    rejectedAmount: 15800,
    pendingAmount: 7500,
    approvalRate: 87.2,
    rejectionRate: 12.8,
    avgClaimValue: 244.8,
  },
];

const InsurancePerformanceChart = ({
  data = DEFAULT_PAYER_PERFORMANCE_DATA,
  selectedPayer,
  onSelectPayer,
}) => {
  const dispatch = useDispatch();
  const reduxActivePayer = useSelector(
    (state) => state?.claimDashboard?.claimDashboardFilters?.insurance || "All",
  );

  const [internalActivePayer, setInternalActivePayer] = useState("All");
  const [sortBy, setSortBy] = useState("netSubmitted");
  const [sortAsc, setSortAsc] = useState(false);

  const currentActivePayer =
    selectedPayer !== undefined && selectedPayer !== "All"
      ? selectedPayer
      : reduxActivePayer !== "All"
        ? reduxActivePayer
        : internalActivePayer;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortBy(field);
      setSortAsc(false);
    }
  };

  const sortedData = useMemo(() => {
    return [...data].sort((a, b) => {
      const factor = sortAsc ? 1 : -1;
      return ((Number(a[sortBy]) || 0) - (Number(b[sortBy]) || 0)) * factor;
    });
  }, [data, sortBy, sortAsc]);

  const maxSubmitted = useMemo(
    () => Math.max(...data.map((d) => Number(d.netSubmitted) || 0), 1),
    [data],
  );

  const handleRowClick = (payerName) => {
    const nextPayer = currentActivePayer === payerName ? "All" : payerName;
    setInternalActivePayer(nextPayer);
    if (onSelectPayer) {
      onSelectPayer(nextPayer);
    }
    dispatch(
      handleClaimDashboardFilter({
        check: "insurance",
        value: nextPayer === "All" ? null : nextPayer,
      }),
    );
  };

  return (
    <Col xs={12} lg={8} className="mt-3">
      <Card className="shadow-sm border h-100">
        {/* Header & Sort Controls */}
        <Card.Header className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between p-3 border-bottom gap-3">
          <div className="d-flex align-items-center gap-2">
            <div
              className="d-flex align-items-center justify-content-center flex-shrink-0"
              style={{
                width: "30px",
                height: "30px",
                borderRadius: "8px",
                backgroundColor: "rgba(59, 130, 246, 0.12)",
                color: "#3B82F6",
                border: "1px solid rgba(59, 130, 246, 0.25)",
              }}
            >
              <Building size={15} />
            </div>
            <div>
              <div className="d-flex align-items-center gap-2">
                <h6 className="mb-0 fw-bold" style={{ fontSize: "15px" }}>
                  Insurance Company Performance
                </h6>
              </div>
              <div className="text-muted" style={{ fontSize: "12px" }}>
                Click a company to filter the dashboard · sort by a metric
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="p-3 d-flex flex-column justify-content-between">
          {/* Horizontal Bar List */}
          <div
            className="d-flex flex-column gap-2 overflow-auto pe-1"
            style={{ maxHeight: "420px" }}
          >
            {sortedData.map((payer) => {
              const isSelected =
                currentActivePayer === payer.payer ||
                currentActivePayer === payer.code;
              const barWidthPercent = (payer.netSubmitted / maxSubmitted) * 100;

              return (
                <div
                  key={payer.payer}
                  onClick={() => handleRowClick(payer.payer)}
                  className="p-3 rounded border transition-all"
                  style={{
                    cursor: "pointer",
                    backgroundColor: isSelected
                      ? "rgba(59, 130, 246, 0.15)"
                      : "transparent",
                    borderColor: isSelected
                      ? "#3B82F6"
                      : "rgba(148, 163, 184, 0.25)",
                    boxShadow: isSelected
                      ? "0 0 0 2px rgba(59, 130, 246, 0.2)"
                      : "none",
                  }}
                >
                  {/* Row Top: Payer Name & Key Amounts */}
                  <div className="d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-1 mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="fw-bold" style={{ fontSize: "14px" }}>
                        {payer.payer}
                      </span>
                      <span
                        className="px-2 py-0 rounded fw-semibold"
                        style={{
                          fontSize: "11px",
                          backgroundColor: "rgba(148, 163, 184, 0.15)",
                          color: "currentColor",
                        }}
                      >
                        {payer.code}
                      </span>
                      <span
                        className="text-muted"
                        style={{ fontSize: "11px", fontWeight: 500 }}
                      >
                        ({Number(payer.claimsCount).toLocaleString("en-US")}{" "}
                        claims)
                      </span>
                    </div>

                    <div
                      className="d-flex align-items-center gap-3"
                      style={{ fontSize: "12px" }}
                    >
                      <div>
                        <span className="text-muted me-1">Sub:</span>
                        <span className="fw-bold">
                          SAR {(payer.netSubmitted / 1000).toFixed(0)}k
                        </span>
                      </div>
                      <div>
                        <span className="me-1" style={{ color: "#10B981" }}>
                          App:
                        </span>
                        <span className="fw-bold" style={{ color: "#10B981" }}>
                          SAR {(payer.approvedAmount / 1000).toFixed(0)}k
                        </span>
                      </div>
                      <div>
                        <span className="me-1" style={{ color: "#EF4444" }}>
                          Rej:
                        </span>
                        <span className="fw-bold" style={{ color: "#EF4444" }}>
                          SAR {(payer.rejectedAmount / 1000).toFixed(0)}k
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Progress Visual Bar */}
                  <div
                    className="w-100 rounded-pill overflow-hidden d-flex position-relative"
                    style={{
                      backgroundColor: "rgba(148, 163, 184, 0.15)",
                      height: "12px",
                    }}
                  >
                    <div
                      style={{
                        width: `${barWidthPercent * (payer.approvalRate / 100)}%`,
                        backgroundColor: "#10B981",
                        transition: "width 0.3s ease",
                      }}
                      title={`Approved: SAR ${Number(payer.approvedAmount).toLocaleString("en-US")}`}
                    ></div>
                    <div
                      style={{
                        width: `${barWidthPercent * (payer.rejectionRate / 100)}%`,
                        backgroundColor: "#EF4444",
                        transition: "width 0.3s ease",
                      }}
                      title={`Rejected: SAR ${Number(payer.rejectedAmount).toLocaleString("en-US")}`}
                    ></div>
                  </div>

                  {/* Row Bottom: Badges */}
                  <div
                    className="mt-2 d-flex align-items-center justify-content-between"
                    style={{ fontSize: "11px" }}
                  >
                    <div className="d-flex align-items-center gap-3">
                      <span
                        className="d-flex align-items-center gap-1 fw-semibold"
                        style={{ color: "#10B981" }}
                      >
                        <CheckCircleFill
                          size={12}
                          style={{ color: "#10B981" }}
                        />
                        Approval: {payer.approvalRate}%
                      </span>
                      <span
                        className="d-flex align-items-center gap-1 fw-semibold"
                        style={{ color: "#EF4444" }}
                      >
                        <XCircleFill size={12} style={{ color: "#EF4444" }} />
                        Rejected: {payer.rejectionRate}%
                      </span>
                    </div>
                    <span className="text-muted fw-medium">
                      Avg Claim: SAR {payer.avgClaimValue}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Card.Body>
      </Card>
    </Col>
  );
};

export default InsurancePerformanceChart;
